import * as React from 'react';
import { MaintenanceHistoryListCard } from './MaintenanceHistoryListCard';
import { NoMaintenanceHistory } from './NoMaintenanceHistory';
import { MaintenanceHistoryContext } from 'app/components/ReactContexts/maintenanceHistoryContext';
import { useTranslation } from 'react-i18next';
import * as utils from '../maintenanceUtils';
import { useParams } from 'react-router-dom';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { isAbortError } from 'errors/isAbortError';
import { axiosInstance } from 'services/api';
import { MaintenanceHistory } from 'types/MaintenanceHistory';
import './MaintenanceHistoryList.less';
import {
  BulkActionsToolbar,
  useSelectionContext,
} from 'app/components/bulk-actions';
import { useUndo } from 'context';
import { maintenanceApi } from 'services/api/maintenance/queries';
import { DeleteDialog } from 'app/components/dialogs/DeleteDialog';
import { List } from 'app/components/List';

interface Props {
  isBulkDeleteAllowed: boolean;
  equipmentId: string;
  openEngineHoursUpdateDialog: boolean;
  setOpenEngineHoursUpdateDialog: any;
}

export function MaintenanceHistoryList({
  isBulkDeleteAllowed,
  equipmentId,
  openEngineHoursUpdateDialog,
  setOpenEngineHoursUpdateDialog,
}: Props) {
  const { t } = useTranslation();
  const params = useParams();

  const { equipmentListData } = React.useContext(EquipmentListContext);
  const { maintenanceHistoryData, setMaintenanceHistoryData } =
    React.useContext(MaintenanceHistoryContext);
  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const [modelIsLoading, setModelIsLoading] = React.useState<boolean>(false);

  const { clearSelection } = useSelectionContext<MaintenanceHistory>();
  const { executeAction } = useUndo();

  const [deleteMaintenanceHistory, { isLoading: isDeleting }] =
    maintenanceApi.useDeleteMaintenanceHistoryMutation();
  const [restoreMaintenanceHistory, { isLoading: isRestoring }] =
    maintenanceApi.useRestoreMaintenanceHistoryMutation();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
    React.useState<boolean>(false);
  const [pendingDeleteIds, setPendingDeleteIds] = React.useState<
    Set<string | number>
  >(new Set());

  React.useEffect(() => {
    !maintenanceHistoryData.selectedEquipmentMaintenanceHistory &&
      (async () => {
        const equipment = utils.getSelectedEquipment(
          equipmentListData,
          params.id,
        );

        try {
          setModelIsLoading(true);
          const maintenanceHistoryResponse = await axiosInstance.get(
            `/apiv2/user/equipment/${params.id}/maintenance`,
          );

          const maintenanceScheduleResponse = await axiosInstance.get(
            `/api/maintenanceSchedule/${equipment?.model}`,
            {
              headers: {
                version: '2022_R03',
              },
            },
          );

          setMaintenanceHistoryData({
            ...maintenanceHistoryData,
            selectedEquipmentMaintenanceHistory:
              utils.getSortedMaintenanceHistory(
                maintenanceHistoryResponse.data,
              ),
            rawMaintenanceIntervals: maintenanceScheduleResponse.data,
            maintenanceIntervals: utils.getProcessedMaintenanceSchedule(
              maintenanceScheduleResponse.data,
              true,
            ),
          });
          setModelIsLoading(false);
        } catch (err: any) {
          if (isAbortError(err)) return;

          addErrorModal();
        }
      })();
  }, [equipmentListData.selectedEquipment]);

  const handleDelete = async (selectedIds: Set<string | number>) => {
    const previousHistory =
      maintenanceHistoryData.selectedEquipmentMaintenanceHistory || [];

    const updatedHistory = previousHistory.filter(
      item => !selectedIds.has(item.id),
    );

    await executeAction({
      label: t('track_maintenance_page.bulk_actions.maintenance_deleted', {
        n: selectedIds.size,
      }),
      strategy: 'IMMEDIATE',
      action: async () => {
        await deleteMaintenanceHistory({
          id: equipmentId,
          payload: {
            uuids: Array.from(selectedIds).map(id => String(id)),
          },
        }).unwrap();

        clearSelection();

        setMaintenanceHistoryData({
          ...maintenanceHistoryData,
          selectedEquipmentMaintenanceHistory: updatedHistory,
        });
      },
      undoAction: async () => {
        await restoreMaintenanceHistory({
          id: equipmentId,
          payload: {
            uuids: Array.from(selectedIds).map(id => String(id)),
          },
        }).unwrap();

        setMaintenanceHistoryData({
          ...maintenanceHistoryData,
          selectedEquipmentMaintenanceHistory: previousHistory,
        });
      },
    });
  };

  const handleOpenDeleteDialog = (selectedIds: Set<string | number>) => {
    if (selectedIds.size === 0) return;

    setPendingDeleteIds(new Set(selectedIds));
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setPendingDeleteIds(new Set());
  };

  const handleConfirmDeleteDialog = async () => {
    if (isDeleting || isRestoring) return;

    const idsToDelete = new Set(pendingDeleteIds);
    if (idsToDelete.size === 0) return;

    handleCloseDeleteDialog();
    await handleDelete(idsToDelete);
  };

  return (
    <>
      <DeleteDialog
        open={isDeleteDialogOpen}
        title={t('track_maintenance_page.delete_dialog.title')}
        description={
          <>
            <span className="maintenance-history-list__delete-dialog-description-item">
              {t('track_maintenance_page.delete_dialog.are_you_sure', {
                count: pendingDeleteIds.size,
              })}
            </span>
            <span>
              {t(
                'track_maintenance_page.delete_dialog.this_action_cannot_be_undone',
              )}
            </span>
          </>
        }
        confirmationLabel={t('bulk_actions.delete')}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteDialog}
      />

      {maintenanceHistoryData.selectedEquipmentMaintenanceHistory &&
        maintenanceHistoryData.selectedEquipmentMaintenanceHistory?.length >
          0 && (
          <div className="info-panel-list-header">
            <h3>{t('maintenance_history_title')}</h3>
          </div>
        )}
      {modelIsLoading ? (
        <div className="loading loading-attachments">
          <div className="loading_text">{t('loading_maintenance_history')}</div>
          <div className="loading_icon">
            <div className="css-icon"></div>
          </div>
        </div>
      ) : (
        <div className="info-panel-list info-panel-list-history">
          <div className="info-panel-list-items">
            {maintenanceHistoryData?.selectedEquipmentMaintenanceHistory
              ?.length ? (
              <>
                {isBulkDeleteAllowed && (
                  <BulkActionsToolbar
                    className="maintenance-history-list__bulk-actions-toolbar"
                    items={
                      maintenanceHistoryData.selectedEquipmentMaintenanceHistory
                    }
                    disabled={isDeleting || isRestoring}
                    onDelete={handleOpenDeleteDialog}
                  />
                )}

                <List variant="flush">
                  {maintenanceHistoryData.selectedEquipmentMaintenanceHistory.map(
                    (item, index) => (
                      <MaintenanceHistoryListCard
                        maintenanceItem={item}
                        index={index}
                        key={item.id}
                        disabled={isDeleting || isRestoring}
                        isSelectionEnabled={isBulkDeleteAllowed}
                        openEngineHoursUpdateDialog={
                          openEngineHoursUpdateDialog
                        }
                        setOpenEngineHoursUpdateDialog={
                          setOpenEngineHoursUpdateDialog
                        }
                        deleteMaintenanceHistory={() =>
                          handleDelete(new Set([item.id]))
                        }
                      />
                    ),
                  )}
                </List>
              </>
            ) : (
              <NoMaintenanceHistory />
            )}
          </div>
        </div>
      )}
    </>
  );
}
